import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { orderService } from '../../services/order.service';
import { theme } from '../../config/theme';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ArrowLeftIcon, PackageIcon, MapPinIcon, UserIcon } from '../../components/icons/Icons';

const StatusBadge = ({ status, size = 'sm' }) => {
  const color = getStatusColor(status);
  const label = status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  return (
    <span style={{
      display: 'inline-block',
      padding: size === 'lg' ? '6px 16px' : '4px 12px',
      borderRadius: theme.borderRadius.full,
      fontSize: size === 'lg' ? theme.typography.fontSize.sm : theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.semibold,
      color: '#fff', backgroundColor: color,
    }}>{label}</span>
  );
};

const AdminOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await orderService.getOrderById(id);
      const orderData = response.data?.data?.order || response.data?.order || null;
      setOrder(orderData);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <MainLayout><LoadingSpinner fullScreen /></MainLayout>;
  if (!order) return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, textAlign: 'center' }}>
        <h2>Order not found</h2>
        <Button variant="primary" onClick={() => navigate('/admin/orders')} style={{ marginTop: theme.spacing.lg }}>
          Back to Orders
        </Button>
      </div>
    </MainLayout>
  );

  const shippingAddress = order.shipping_address || {};

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.xl, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/admin/orders')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: theme.spacing.xs }}>
            <ArrowLeftIcon size={24} color={theme.colors.text.secondary} />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: isMobile ? theme.typography.fontSize.xl : theme.typography.fontSize['2xl'], fontWeight: 'bold', margin: 0 }}>
              Order #{order.order_number}
            </h1>
            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
              {formatDate(order.createdAt || order.created_at, 'long')}
            </span>
          </div>
          <StatusBadge status={order.status} size="lg" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: theme.spacing.xl }}>
          {/* Left Column */}
          <div>
            {/* Items */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.xl }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.lg }}>
                <PackageIcon size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Order Items ({order.items?.length || 0})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                {order.items?.map(item => (
                  <div key={item.id} style={{
                    display: 'flex', gap: theme.spacing.md, padding: theme.spacing.md,
                    borderRadius: theme.borderRadius.lg, backgroundColor: theme.colors.gray[50],
                  }}>
                    {item.product_image ? (
                      <img src={item.product_image} alt={item.product_name}
                        style={{ width: 64, height: 64, borderRadius: theme.borderRadius.md, objectFit: 'cover' }} />
                    ) : (
                      <div style={{
                        width: 64, height: 64, borderRadius: theme.borderRadius.md,
                        backgroundColor: theme.colors.gray[200], display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}><PackageIcon size={24} color={theme.colors.gray[400]} /></div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: 2 }}>{item.product_name}</div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                        Qty: {item.quantity} × {formatCurrency(item.price)}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, flexWrap: 'wrap', gap: 4 }}>
                        <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
                          <StatusBadge status={item.fulfillment_status} />
                          {item.seller && (
                            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled }}>
                              Seller: {item.seller?.store_name || item.seller?.user?.first_name || 'N/A'}
                            </span>
                          )}
                        </div>
                        <span style={{ fontWeight: theme.typography.fontWeight.bold }}>{formatCurrency(item.subtotal)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Right Column */}
          <div>
            {/* Customer Info */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                <UserIcon size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Customer
              </h3>
              <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, lineHeight: 1.7 }}>
                <div style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                  {order.user?.first_name} {order.user?.last_name}
                </div>
                <div>{order.user?.email}</div>
              </div>
            </Card>

            {/* Order Summary */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                Order Summary
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                  <span>Subtotal</span><span>{formatCurrency(order.subtotal)}</span>
                </div>
                {parseFloat(order.tax) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span>Tax</span><span>{formatCurrency(order.tax)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  borderTop: `1px solid ${theme.colors.gray[200]}`, paddingTop: theme.spacing.sm,
                  fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.lg,
                }}>
                  <span>Total</span><span style={{ color: theme.colors.primary.main }}>{formatCurrency(order.total)}</span>
                </div>
              </div>
            </Card>

            {/* Shipping Address */}
            <Card padding="lg">
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                <MapPinIcon size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Shipping Address
              </h3>
              <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, lineHeight: 1.7 }}>
                <div style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                  {shippingAddress.first_name} {shippingAddress.last_name}
                </div>
                <div>{shippingAddress.address_line1}</div>
                {shippingAddress.address_line2 && <div>{shippingAddress.address_line2}</div>}
                <div>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}</div>
                {shippingAddress.phone_number && <div>Phone: {shippingAddress.phone_number}</div>}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminOrderDetails;
